import React from 'react'
import PropTypes from 'prop-types'
import styled, { withTheme } from 'styled-components'
import { Link } from 'react-router'
import { take, find, partition } from 'lodash'
import InlineLoader from '../InlineLoader/InlineLoader'
import useIsMobile from '../../hooks/useBreakpoint'

const Wrapper = styled.div`
  margin: 25px 0px;

  h2 {
    margin: 5px 0 1.5rem;
    font-weight: bold;
  }

  h2.big {
    font-size: var(--h4);
    color: var(--clr-grey-4-to-white);
    margin: 0 10% 2rem;
    text-align: center;
  }

  h2.small {
    font-size: var(--h6);
    color: var(--clr-black-to-text);
    width: 70%;
    line-height: 1.5;
    height: 50px;
  }

  a {
    color: var(--clr-accent);
    display: block;
    text-decoration: underline;
    margin-bottom: 5px;
    text-decoration: none;
  }

  a:hover {
    text-decoration: underline;
  }

  .related-blocks {
    align-items: flex-start;
    gap: 20px;
  }

  .related-bar {
    flex-basis: 150px;
    width: 95%;
    flex-grow: 1;
    align-items: flex-start;
  }

  .vertical {
    flex-direction: column;

    h2.small {
      height: unset;
    }
  }

  .faqs-link,
  .guides-link,
  .videos-link {
    margin-bottom: 5px;
  }

  @media (max-width: 768px) {
    h2.small {
      height: auto;
    }

    related-blocks {
      align-items: center;
    }

    .flex {
      justify-content: unset;
    }
  }

  h2:not(:first-child) {
    margin-top: var(--xl);
  }
`

const RELATED_CONTENT_DISPLAY_COUNT = 4

const RelatedSidebarLink = ({ link, query, createUserEvent, linkType }) => {
  return (
    <Link
      to={{
        pathname: `/${link.to}/${link.pk}`,
        query: {
          ...query,
          device: link.device || query.device
        }
      }}
      onClick={() =>
        createUserEvent({
          category: 'navigation',
          data: {
            link_to: `/${link.to}/${link.pk}`,
            source: `Related-${linkType}`
          }
        })
      }
      className={`${linkType}-link`}
    >
      {link.name}
    </Link>
  )
}

const RelatedSidebarInfo = ({
  related,
  query,
  createUserEvent,
  relatedType,
  isSameDevice
}) => {
  const data = isSameDevice ? related.data[0] : related.data[1]
  if (!related.title || !data.length) {
    return null
  }
  return (
    <div className="related-bar flex column">
      <h2 className="small">{related.title}</h2>
      {data.slice(0, RELATED_CONTENT_DISPLAY_COUNT).map((link, i) => (
        <RelatedSidebarLink
          link={link}
          query={query}
          key={`related_info_${i}`}
          createUserEvent={createUserEvent}
          relatedType={relatedType}
          linkType={link.to}
        />
      ))}
    </div>
  )
}

const RelatedSidebar = ({
  data,
  query,
  createUserEvent,
  relatedType,
  contentType,
  isFetching
}) => {
  if (isFetching) {
    return <InlineLoader style={{ margin: '0 auto', display: 'block' }} />
  }

  const [isMobile] = useIsMobile()
  const isVertical = isMobile || contentType !== 'tutorial'
  const [header, ...rest] = data
  const related = rest.map(topic => ({
    ...topic,
    data: partition(topic.data, 'isSameDevice')
  }))
  const hasDifferentDeviceData = Boolean(
    find(related, o => o.data[1].length > 0)
  )

  return (
    <Wrapper className="related-sidebar animated fadeIn">
      <h2 className="big">{header.title}</h2>
      <div className={`flex related-blocks ${isVertical ? 'vertical' : ''}`}>
        {take(related, RELATED_CONTENT_DISPLAY_COUNT).map((related, index) => (
          <RelatedSidebarInfo
            related={related}
            isSameDevice={true}
            query={query}
            index={index}
            key={`related_${index}`}
            createUserEvent={createUserEvent}
            relatedType={relatedType}
          />
        ))}
      </div>
      {isVertical && hasDifferentDeviceData && (
        <>
          <h2 className="big">{header.otherTitle}</h2>
          <div className="flex related-blocks vertical">
            {take(related, RELATED_CONTENT_DISPLAY_COUNT).map(
              (related, index) => (
                <RelatedSidebarInfo
                  related={related}
                  isSameDevice={false}
                  query={query}
                  index={index}
                  key={`related_${index}`}
                  createUserEvent={createUserEvent}
                  relatedType={relatedType}
                />
              )
            )}
          </div>
        </>
      )}
    </Wrapper>
  )
}

RelatedSidebar.propTypes = {
  data: PropTypes.arrayOf({
    title: PropTypes.string.isRequired,
    big: PropTypes.bool,
    data: PropTypes.arrayOf(
      PropTypes.shape({
        device: PropTypes.string,
        isSameDevice: PropTypes.bool,
        name: PropTypes.string.isRequired,
        pk: PropTypes.number.isRequired,
        to: PropTypes.string.isRequired
      })
    ),
    query: PropTypes.shape({
      device: PropTypes.string.isRequired
    }),
    createUserEvent: PropTypes.func.isRequired,
    relatedType: PropTypes.string.isRequired,
    contentType: PropTypes.string.isRequired,
    isFetching: PropTypes.bool.isRequired
  })
}.isRequired

export default withTheme(RelatedSidebar)



// WEBPACK FOOTER //
// ./src/components/RelatedSidebar/RelatedSidebar.js